import React, { FormEventHandler, useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { m } from 'framer-motion';
import './Register.scss';
import aside from '../../assets/img/register1.jpg';
import InputText from 'components/InputText/InputText';
import { Button } from 'components/Button/Button';
import { AnchorText } from 'components/AnchorText/AnchorText';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import Spinner from '../Spinner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';
import { routes } from '../../routing/routes';
import { deleteReferralId, getReferralId } from 'contexts/UserReferralContext';
import { gtag } from '../../utils/analytics';
import { useAccount } from 'contexts/AccountContext';
import { Link } from 'react-router-dom';

const sitekey = process.env.REACT_APP_HCAPTCHA_SITEKEY;

const submitSpinner = <Spinner color="white" width={20} height={20} />;

const Register = () => {
  const navigate = useNavigate();
  const emailRef = useRef<HTMLInputElement>();
  const passwordRef = useRef<HTMLInputElement>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [hcaptchaResponse, setHcaptchaResponse] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [agree, setAgree] = useState(false);
  const { ethereum } = useUser();
  const { playfab } = useAccount();

  useEffect(() => {
    if (!ethereum.isConnecting && !ethereum.isConnected) {
      navigate(routes.login());
    }
  }, [ethereum, navigate]);

  const submit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const referralCode = getReferralId();
      await playfab.register(email, password, referralCode, hcaptchaResponse);
      gtag('event', 'register successful');
      deleteReferralId();
      navigate(routes.mint());
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorString = err.response?.data?.message;
        if (errorString) {
          gtag('event', 'register error');
          setErrorMessage(errorString)
        }
      } else {
        if (err instanceof Error) 
          setErrorMessage(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const tosHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgree(event.target.checked);
  };

  const holder = {
    background: 'url(' + aside + ') no-repeat center center',
    backgroundSize: 'cover',
  };

  return (
    <div className="register-container ">
      <m.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="account"
      >
        <Helmet>
          <title>Register</title>
          <meta name="description" content="Create an account and start playing!" />
        </Helmet>

        <section className="account-container has-bg">
          <form onSubmit={submit}>
            <div className="container">
              <div className="row p-0 g-0 justify-content-center">
                <div className="col has-bg">
                  <div className="is-bg no-overlay" style={holder}></div>
                </div>

                <div className="col-xs-12 col-sm-7 p-4 background-400">
                  <h2 className="text-center mb-2 mt-2">Register Breaker ID</h2>
                  <p className="text-center">
                    Creating a Breaker ID will give you an enhanced gaming experience.
                  </p>

                  <HorizontalDivider />
                  {errorMessage !== '' &&
                    <div className="alert alert-danger">
                      {errorMessage}
                    </div>
                  }

                  <div className="form-elements">
                    <div>
                      <InputText
                        id="email-input"
                        label="Email"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                        }}
                        type="email"
                        placeholder="you@domain.com"
                        ref={emailRef}
                      />
                    </div>

                    <div>
                      <InputText
                        id="password-input"
                        label="Password"
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value);
                        }}
                        type="password"
                        placeholder="********"
                        message="Must be at least 6 characters"
                        minLength={6}
                        ref={passwordRef}
                      />
                    </div>
                    {sitekey && (
                      <div className="captcha-container">
                        <HCaptcha
                          sitekey={sitekey}
                          theme="dark"
                          onVerify={(response) => setHcaptchaResponse(response)}
                        />
                      </div>
                    )}

                    <Button
                      primary
                      fullWidth
                      disabled={
                        !agree ||
                        submitting ||
                        !emailRef.current?.validity.valid ||
                        !passwordRef.current?.validity.valid ||
                        !hcaptchaResponse
                      }
                    >
                      {submitting ? submitSpinner : 'Register'}
                    </Button>
                  </div>
                  <p className="form-footer-disclaimer">
                    <input type="checkbox" className="form-check-input" onChange={tosHandler} />
                    by registering a DuskBreakersID you agree to our
                    <AnchorText href={routes.nftTerms()}>terms</AnchorText>, which include NFT
                    license terms.
                  </p>
                  <p className="form-footer">
                    Already have an account?
                    <Link to={routes.loginDusktech()} className="anchor-text">
                      Login Here
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </form>
        </section>
      </m.main>
    </div>
  );
};

export default Register;
